/**
 * Catalog item — shared replenishable physical inventory unit.
 * Maps from boxed-backend AssetGroup + FulfillableAsset (flattened).
 *
 * Field mapping (boxed -> catalog):
 *   AssetGroupDdb.pk                         -> id
 *   AssetGroupProps.name / asset.name        -> name
 *   asset.image                              -> image
 *   AssetGroupProps.overrideImage            -> overrideImage
 *   asset.id (protocol-specific)             -> sourceId
 *   asset.value (kill/EV)                    -> value
 *   InventoryDetails.marketPrice             -> marketPrice
 *   asset.compareAtValue                     -> compareAtValue
 *   asset.compareAtLabelOverride             -> compareAtLabelOverride
 *   AssetGroupProps.quantity                 -> quantity (facility on-hand)
 *   AssetGroupDdb.withdrawingCount           -> withdrawingCount
 *   InventoryDetails.listingCount            -> listingCount
 *   InventoryDetails.userInventoryCount      -> userInventoryCount
 *   AssetGroupProps.fulfillableStatus        -> fulfillableStatus
 *   InventoryDetails.overrideFulfillableStatus -> overrideFulfillableStatus
 *   Protocol TCG vs CUSTOM_TCG               -> availabilityStrategy (DYNAMIC vs FIXED)
 *   AssetGroupProps.tags                     -> tags
 *   asset.details / filter                   -> attributes / pricingRef
 *   asset.collectionName                     -> attributes.collectionName (OS projection)
 */

import type { CatalogItemStatus, Circulation } from "@loot-os/core/db/schema";

export type PricingSource = "TCGPLAYER" | "MANUAL" | "STOCKX" | "FANATICS" | "SHOPIFY" | "OTHER";

export const PRICING_SOURCES = [
  "TCGPLAYER",
  "MANUAL",
  "STOCKX",
  "FANATICS",
  "SHOPIFY",
  "OTHER",
] as const satisfies readonly PricingSource[];

export function isPricingSource(value: string): value is PricingSource {
  return (PRICING_SOURCES as readonly string[]).includes(value);
}

/** Every fulfillable status, in one list so validators derive from the type itself. */
export const FULFILLABLE_STATUSES = [
  "NO_RISK",
  "MEDIUM_RISK",
  "HIGH_RISK",
  "UNFULFILLABLE",
  "SUPER_UNFULFILLABLE",
  "ARCHIVED",
] as const;

export type FulfillableStatus = (typeof FULFILLABLE_STATUSES)[number];

/**
 * Matches boxed `AvailabilityStrategy` — whether we choose to source as needed
 * or require on-hand stock before an item can be in a box.
 * DYNAMIC = OK sourcing as needed (former TCG / soft-cap).
 * FIXED = want on hand before box use; once qty is depleted it can't be used in
 * a box (former CUSTOM_TCG / hard-cap).
 */
export const AVAILABILITY_STRATEGIES = ["DYNAMIC", "FIXED"] as const;
export type AvailabilityStrategy = (typeof AVAILABILITY_STRATEGIES)[number];

export function isAvailabilityStrategy(value: string): value is AvailabilityStrategy {
  return (AVAILABILITY_STRATEGIES as readonly string[]).includes(value);
}

export interface CatalogItem {
  id: string;
  name: string;
  image: string | null;
  overrideImage: string | null;
  /** Protocol-specific source id (e.g. tcgplayer product#printing). */
  sourceId: string | null;
  /** Kill / display value in cents (boxed asset.value). */
  value: number | null;
  marketPrice: number | null;
  /** Display-only strikethrough worth; presence marks a discounted item. */
  compareAtValue: number | null;
  /** Optional freeform strikethrough label override. */
  compareAtLabelOverride: string | null;
  pricingSource: PricingSource;
  /** e.g. { productId, printing } for price-sync. */
  pricingRef: Record<string, unknown> | null;
  /** DYNAMIC = source as needed; FIXED = require on-hand before box use. */
  availabilityStrategy: AvailabilityStrategy;
  /** Facility stock on hand (source of truth; boxed AssetGroupProps.quantity). */
  quantity: number;
  /** In-flight withdrawals / reservations (boxed withdrawingCount). */
  withdrawingCount: number;
  /** Known marketplace listing count feeding fulfillability. */
  listingCount: number | null;
  /** Denormalized user-inventory signal (boxed InventoryDetails.userInventoryCount). */
  userInventoryCount: number | null;
  fulfillableStatus: FulfillableStatus;
  overrideFulfillableStatus: FulfillableStatus | null;
  /** Lifecycle, separate from the risk grade; see `lifecycle.ts`. */
  status: CatalogItemStatus;
  /** Policy circulation as the rules' owner (Boxed) computed it; read via `effectiveCirculation`. */
  circulation: Circulation;
  /** Admin override of `circulation`; PRE_RELEASE and ARCHIVED still win. */
  overrideCirculation: Circulation | null;
  /** Archive once the last unit ships. */
  autoArchive: boolean;
  /** Data-driven taxonomy: TCG, Apparel, Sports, Collectibles, etc. */
  category: string | null;
  subcategory: string | null;
  /** Singles, Sealed Product, etc. — usable for shipping rules later. */
  type: string | null;
  tags: string[];
  /** Protocol-specific details; optional `collectionName` (TCG set display name). */
  attributes: Record<string, unknown> | null;
  isInForge: boolean;
  isInMarketplace: boolean;
  /** NULL = shared, read by every tenant; set = bound to that tenant, which alone reads it. */
  tenantId: string | null;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * What {@link buildCatalogItem} accepts: a name and its pricing rules, the rest defaulted.
 * `createdAt` is when the item first existed in the writer's own store (Boxed's asset group);
 * it defaults to now and is read only on create.
 */
export type CreateCatalogItemInput = Partial<Omit<CatalogItem, "updatedAt">> &
  Pick<CatalogItem, "name" | "pricingSource" | "availabilityStrategy">;

export type UpdateCatalogItemInput = Partial<Omit<CatalogItem, "id" | "createdAt" | "updatedAt">>;

const COMPARE_AT_LABEL_MAX_LENGTH = 40;

/**
 * Ported from boxed `validateCompareAtFields`. Guards discounted-item authoring:
 * compareAtValue must be positive and strictly greater than value; a label
 * override requires a compareAtValue and is length-capped.
 */
export function validateCompareAtFields(
  item: Pick<CatalogItem, "value" | "compareAtValue" | "compareAtLabelOverride">,
): void {
  if (item.compareAtValue != null) {
    if (item.compareAtValue <= 0 || item.compareAtValue <= (item.value ?? 0)) {
      throw new Error("compareAtValue must be a positive number greater than the item's value");
    }
  }
  if (item.compareAtLabelOverride != null && item.compareAtValue == null) {
    throw new Error("compareAtLabelOverride requires compareAtValue");
  }
  if (
    item.compareAtLabelOverride != null &&
    item.compareAtLabelOverride.length > COMPARE_AT_LABEL_MAX_LENGTH
  ) {
    throw new Error(
      `compareAtLabelOverride must be ${COMPARE_AT_LABEL_MAX_LENGTH} characters or fewer`,
    );
  }
}

export function assertCatalogItemInvariants(item: CatalogItem): void {
  if (!item.name?.trim()) {
    throw new Error("Catalog item name is required");
  }
  if (item.value != null && item.value < 0) {
    throw new Error("Catalog item value cannot be negative");
  }
  if (item.marketPrice != null && item.marketPrice < 0) {
    throw new Error("Catalog item marketPrice cannot be negative");
  }
  if (item.quantity < 0) {
    throw new Error("Catalog item quantity cannot be negative");
  }
  if (item.withdrawingCount < 0) {
    throw new Error("Catalog item withdrawingCount cannot be negative");
  }
  validateCompareAtFields(item);
}
